// @ts-nocheck
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getBillTosDataView,
    getCurrentBillToState,
} from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Select, { SelectPresentationProps } from "@store/MobiusOverrides/Select/Select";
import constants from "@store/constants";
import { useUserNotificationPreferencesContext } from "@store/Framework/Components/UserNotificationPreferencesContext/UserNotificationPreferencesContext";
import asString from "@store/Framework/Common/Utilities/asString";
import validateRequiredDropdown from "@store/Framework/Common/Utilities/validateRequiredDropdown";
import setFieldValue from "@store/Framework/Store/Greif/Components/InviteYourColleagueForm/Handlers/SetFieldValue";
import { InviteYourColleagueFormContext } from "@store/Library/Widgets/InviteYourColleague/InviteYourColleagueForm";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import React, { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    isRequired = "isRequired",
    fieldLabel = "fieldLabel",
    soldTosToDisplay = "soldTosToDisplay",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.isRequired]: boolean;
        [fields.fieldLabel]: string;
        [fields.soldTosToDisplay]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    soldTo: asString(state.greif.components.inviteYourColleagueForm.fieldValues["soldTo"]),
    billToDataview: getBillTosDataView(state, {
        page: 1,
        pageSize: Number(props.fields.soldTosToDisplay),
    }),
    currentBillToState: getCurrentBillToState(state),
});

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentBillTo,
    loadBillTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SoldToStyles {
    soldToTextField?: TextFieldPresentationProps;
    soldToSelect?: SelectPresentationProps;
}

export const soldToStyles: SoldToStyles = {};

const styles = soldToStyles;

const SoldTo: FC<Props> = ({
    fields,
    soldTo,
    currentBillToState,
    billToDataview,
    setFieldValue,
    loadBillTos,
    loadCurrentBillTo,
}) => {
    const { isRequired, fieldLabel, soldTosToDisplay } = fields;
    const inviteYourColleagueFormContext = useContext(InviteYourColleagueFormContext);
    const [soldToErrorMessage, setSoldToErrorMessage] = useState("");
    const [hasValidated, setHasValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [soldToData, setSoldToData] = useState<any[]>();
    const { customerSoldTo } = useUserNotificationPreferencesContext();

    const { h } = useQueryString();

    const currentBillTo = currentBillToState && currentBillToState.value;
    const allBillToDataview = billToDataview && billToDataview.value;

    const params: GetBillTosApiParameter = {
        page: 1,
        pageSize: Number(fields.soldTosToDisplay),
    };

    useEffect(() => {
        if (h && customerSoldTo) {
            setSoldToData(customerSoldTo.soldTos);
        }

        if (!billToDataview.isLoading && !billToDataview.value && !h) {
            loadCurrentBillTo();
            loadBillTos(params);
        }

        if (allBillToDataview && currentBillTo && !h) {
            setSoldToData(billToDataview.value);
            setFieldValue({ key: "soldTo", value: currentBillToState.value.customerNumber });
        }
    }, [customerSoldTo, currentBillToState.value, billToDataview.value]);

    useEffect(() => {
        if (typeof soldTo === "undefined") {
            if (currentBillTo) {
                setFieldValue({ key: "soldTo", value: currentBillToState.value.customerNumber });
            }
            if (h) {
                setFieldValue({ key: "soldTo", value: "" });
            }
        }
    }, [soldTo]);

    useEffect(() => {
        inviteYourColleagueFormContext.validators.soldTo = validateSoldTo;
    }, [soldTo]);

    useEffect(() => {
        if (hasValidated) {
            validateSoldTo();
        }
    }, [soldTo, hasValidated]);

    useEffect(() => {
        if (billToDataview?.value) {
            const sortedSoldToData = currentBillTo
                ? [
                      currentBillTo,
                      ...billToDataview.value.filter(item => item.customerNumber !== currentBillTo.customerNumber),
                  ]
                : [...billToDataview.value];

            setSoldToData(sortedSoldToData);
        }
    }, [billToDataview.value, currentBillTo]);

    const validateSoldTo = (value: string = soldTo || "") => {
        setHasValidated(true);
        const errorMessage = validateRequiredDropdown(value || "", fieldLabel);
        setSoldToErrorMessage(errorMessage || "");
        return !errorMessage;
    };

    const soldToChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "shipTo", value: "" });
        setFieldValue({ key: "soldTo", value: event.target.value });
        if (hasValidated) {
            validateSoldTo(event.target.value);
        }
    };

    return (
        <>
            <Select
                label={fieldLabel}
                value={soldTo || ""}
                onChange={soldToChangeHandler}
                required={isRequired}
                error={soldToErrorMessage}
                disabled={!soldToData}
                {...styles.soldToSelect}
                data-test-selector="sold-to-input"
            >
                <option value="">{translate("Select a Sold-To")}</option>
                {soldToData?.map((soldTo, id) => (
                    <option key={id} value={soldTo.customerNumber}>
                        {soldTo.customerNumber === currentBillTo?.customerNumber
                            ? `[${translate("CURRENT")}] ${soldTo.label}`
                            : soldTo.label}
                    </option>
                ))}
            </Select>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SoldTo),
    definition: {
        displayName: "Sold-To",
        group: constants.widgetGroup.inviteYourColleague,
        icon: "input-pipe",
        allowedContexts: [constants.pageType.inviteYourColleague, constants.pageType.notificationPreferences],
        fieldDefinitions: [
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.fieldLabel,
                displayName: "Field Label",
                editorTemplate: "TextField",
                defaultValue: "Customer Sold-To",
                fieldType: "Translatable",
            },
            {
                name: fields.soldTosToDisplay,
                displayName: "SoldTos to display",
                editorTemplate: "IntegerField",
                max: 250,
                defaultValue: 250,
                tooltip: "Platform default: 20",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
